import React from 'react';
import { Avatar, Box, Button, Flex, Grid, Spinner, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ClientWithLocations } from '@foodfluence/shared';
import { ff } from '../../../theme';

export type BrandItem = { client: ClientWithLocations; isNew: boolean };

const BrandAvatarBase: React.FC<BrandItem> = ({ client, isNew }) => {
  const navigate = useNavigate();
  return (
    <Box
      as="button"
      type="button"
      aria-label={`Open ${client.accountName}${isNew ? ' (new brand)' : ''}`}
      onClick={() => navigate(`/explore/client/${client.id}`)}
      flex="none"
      w="64px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="6px"
      color={ff.ink}
    >
      <Flex w="64px" h="64px" borderRadius="full" p="3px" bg={isNew ? ff.lime : ff.line}>
        <Avatar
          src={client.instagramProfilePicture || client.bannerPicture}
          name={client.accountName}
          w="100%"
          h="100%"
          border="2px solid white"
          bg={ff.grey}
          color={ff.ink}
          sx={{ '& > [role=img]': { fontSize: '16px', fontWeight: 600 } }}
        />
      </Flex>
      <Text w="100%" fontSize="12px" lineHeight="normal" textAlign="center" noOfLines={1}>
        {client.accountName}
      </Text>
    </Box>
  );
};
const BrandAvatar = React.memo(BrandAvatarBase);

/**
 * "Brands to work with": one avatar row (new brands get the lime ring); "See all" expands it
 * in place into a wrapped grid. `endRef` is the pagination sentinel at the end of either layout.
 */
const BrandsRow: React.FC<{
  brands: BrandItem[];
  expanded: boolean;
  onToggle: () => void;
  endRef: (el: HTMLDivElement | null) => void;
  loading: boolean;
}> = ({ brands, expanded, onToggle, endRef, loading }) => {
  if (brands.length === 0) return null;
  const sentinel = (
    <Flex ref={endRef} flex="none" w="24px" minH="64px" align="center" justify="center">
      {loading && <Spinner size="sm" color={ff.ink} />}
    </Flex>
  );
  return (
    <Box as="section" aria-label="Brands to work with">
      <Flex align="center" justify="space-between" mb="10px">
        <Text as="h2" m={0} fontSize="15px" fontWeight={600} lineHeight="normal">
          Brands to work with
        </Text>
        <Button variant="unstyled" display="flex" alignItems="center" minW="auto" minH="32px" h="32px" fontSize="14px" fontWeight={600} color={ff.ink} onClick={onToggle} aria-expanded={expanded}>
          {expanded ? 'Show less' : 'See all'}
        </Button>
      </Flex>
      {expanded ? (
        <Grid templateColumns="repeat(auto-fill, minmax(64px, 1fr))" gap="14px" justifyItems="center">
          {brands.map(b => (
            <BrandAvatar key={b.client.id} {...b} />
          ))}
          {sentinel}
        </Grid>
      ) : (
        <Flex gap="14px" overflowX="auto" mx="-20px" px="20px" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {brands.map(b => (
            <BrandAvatar key={b.client.id} {...b} />
          ))}
          {sentinel}
        </Flex>
      )}
    </Box>
  );
};

export default BrandsRow;
